import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Popover } from '@/components/Popover';

interface ActionMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

/**
 * Small click-outside popover-menu.
 *
 * Reused by Members-Page (per-row action ⋯ menu) and any spot where a compact
 * "one of many actions" affordance is needed.
 */
export function ActionMenu({
  trigger,
  items,
  align = 'right',
}: {
  trigger: ReactNode;
  items: ActionMenuItem[];
  align?: 'left' | 'right';
}) {
  return (
    <Popover
      width={200}
      align={align}
      trigger={({ onClick, ref }) => (
        <div ref={ref} className="inline-block" onClick={onClick}>
          {trigger}
        </div>
      )}
      content={(close) => (
        <div className="p-1">
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              disabled={it.disabled}
              onClick={() => {
                close();
                it.onSelect();
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-sm hover:bg-accent',
                it.destructive && 'text-destructive hover:bg-destructive/10',
                it.disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
              )}
            >
              {it.icon}
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    />
  );
}
